import {showErrorNotification, showSuccessNotification} from "../../global/notifications";
import {buildApprovalFilter} from "./prf-grid-filters";

// The grid element and the attributes on it are read on demand rather than at module scope so
// that the pure row helpers below can be exercised without a DOM.
const grid = () => $('.js-prf-construction-grid');
const getTopRole = () => grid().data('user-role');
const isViewOnly = () => grid().data('view-only') === true;

export default function () {
    createGrid();
    if (getTopRole() !== CREATOR_ROLE)
        setTimeout(function () {
            setInitialFilter();
        }, 250);
}

// Sentinel for a user who holds no PRF approver role - they only ever see their own PRFs.
// It was called 'entry' while the Entry approval tier existed; the tier is gone but the
// "not an approver" case it stood for is not.
const CREATOR_ROLE = 'creator';

const approvalTemplate = (approval) => {
    const approvalHtml = approval.IsApproved
        ? `<div><span class="tag tag--small tag--green">${approval.ApproverName}</span></div>`
        : `<div><span class="tag tag--small tag--red">${approval.Title} </span></div>`;

    return approvalHtml;
}

const createGrid = () => {
    grid().kendoGrid({
        /*mobile: "phone",*/
        resizable: true,
        columns: [
            {
                field: 'id', title: 'PRF Number', attributes: {class: ''}, type: 'number', width: 105,
                template(dataItem) {
                    let idFormatted = dataItem.id.toString().padStart(4, '0');
                    if (dataItem.prfType === 'Construction')
                        return `PRF-${idFormatted}`
                    else
                        return `MRF-${idFormatted}`
                },
                filterable: false
            }, {
                field: 'projectName', title: 'Client Name', attributes: {class: ''}, type: 'string',
                filterable: false
            }, {
                field: 'projectName', title: 'Project Name', attributes: {class: ''}, type: 'string',
                filterable: false
            }, {
                field: 'criteria',
                title: 'Criteria',
                attributes: {class: ''},
                type: 'string',
                template: function (dataItem) {
                    const criteria = dataItem.criteria;
                    if (criteria === null) return "";

                    const criteriaArray = criteria.split(';');
                    let output = "";
                    criteriaArray.forEach(reason => {
                        output += `<div>${reason}</div>`
                    });
                    return output;
                },
                filterable: false
            }, {
                field: 'approvalLevel',
                title: 'Approval Status',
                attributes: {class: ''},
                type: 'string',
                width: 200,
                template: function (dataItem) {
                    let output = '';

                    if (!dataItem.submitted) {
                        output += '<div><span class="tag tag--small">Not Submitted</span></div>';
                    }

                    dataItem.Approvals.forEach((item) => {
                        output += approvalTemplate(item);
                    });

                    return output;
                },
                filterable: isFilterable()
            }, {
                field: 'committeeReview',
                title: 'Committee Approval Required',
                attributes: {class: ''},
                type: 'string',
                width: 215,
                template: function (dataItem) {
                    if (dataItem.committeeReviewRequired === 'Yes') {
                        if (isViewOnly()) {
                            return dataItem.committeeReview ? 'Approved' : 'Not Approved';
                        }
                        return `<div class="switch-container"><label class="switch"> <input class="committee-approval-checkbox" type="checkbox" data-prf-id="${dataItem.id}" data-prf-type="${dataItem.prfType}" ${dataItem.committeeReview ? "checked" : ""}/><div class="switch-bg"></div><div class="slider-wrapper"><span class="slider round"></span></div><span class="switch-on-text">Approved</span><span class="switch-off-text">Not Approved</span></label></div>`;
                    }
                    return 'Approval is not required';
                },
                filterable: false
            }
        ], dataSource: {
            autoSync: true, schema: {
                model: {
                    id: 'id', fields: {
                        id: {editable: false, type: 'number'},
                        projectName: {editable: false, type: 'string'},
                        prfType: {editable: false, type: 'string'},
                        committeeReview: {editable: false, type: 'boolean'},
                        submitted: {editable: false, type: 'boolean'},
                        branch: {editable: false, type: 'number'},
                        approvalLevel: {editable: false, type: 'string'},
                        entryApproval: {editable: false, type: 'string'},
                        salesManagerApproval: {editable: false, type: 'string'},
                        branchManagerApproval: {editable: false, type: 'string'},
                        regionalManagerApproval: {editable: false, type: 'string'},
                        cooApproval: {editable: false, type: 'string'},
                        cfoApproval: {editable: false, type: 'string'},
                        cfoApprovalRequired: {editable: false, type: 'string'},
                        ceoApproval: {editable: false, type: 'string'},
                        committeeReviewRequired: {editable: false, type: 'string'}
                    }
                },
                parse(r) {
                    if (r.length < 0) return;
                    if (getTopRole() === CREATOR_ROLE) {
                        return sortByApprovals(r);
                    }
                    return r;
                }
            }, transport: {
                read: {
                    url: '/Sales/PursuitRequestForm/GetPrfGridData',
                    type: 'GET',
                    dataType: 'json'
                }, parameterMap: function (data, type) {
                    let rs = {};
                    if (type === 'read') {
                        return rs;
                    }
                }
            },
            pageSize: 15
        }, dataBound: function (arg) {
            const $grid = arg.sender;
            const $element = $grid.element;
            const $rows = $element.find('tbody [role="row"]');
            rowClickEvent($rows);
            setCommitteeBackground();
            committeeReviewApprovalEvent();
        },
        pageable: {
            pageSizes: [5, 15, 25, 50, 100, 'all']
        },
        mobile: true,
        scrollable: true,
        sortable: {
            mode: 'multiple'
        },
        filterable: true,
        editable: false,
        columnMenu: false,
        filter: function (e) {
            setCustomFilters(e);
        },
        noRecords: {
            template: "There are no pursuit request forms"
        }
    })
}

const rowClickEvent = ($rows) => {
    $rows.css('cursor', 'pointer');

    $rows.on('click', function (event) {
        const $target = $(event.target)
        const $column = $target.parents('[data-field]');
        const columnField = $column.data('field') || $target.data('field');
        if (columnField !== 'committeeReview') {
            const $self = $(this);
            const data = grid().data('kendoGrid').dataSource.data().find(function (element) {
                return element.uid === $self.data('uid')
            });

            if (!data) return;
            goToPrf(data.id, data.prfType);
        }
    })
}

const goToPrf = (id, prfType) => {
    window.location.href = `/Sales/PursuitRequestForm/${prfType}?id=${id}`;
}

const committeeReviewApprovalEvent = () => {
    const $checkboxes = $('input[type="checkbox"]');
    $checkboxes.click(function () {
        const $self = $(this);
        const checked = $self.prop('checked')
        committeeReviewApproval($self.data(), checked)
            .then(_ => {
                showSuccessNotification('Completed Committee Approval');
            })
            .catch(_ => {
                $self.prop('checked', !checked)
                showErrorNotification('Failed to save Committee Approval')
            })
            .finally(_ => {
                // setCommitteeBackground($self, checked)
            })
    });
}

const committeeReviewApproval = (data, checked) => {
    return new Promise((resolve, reject) => {
        $.post(`/Sales/PursuitRequestForm/${data.prfType}CommitteeReviewApproval`, {
            id: data.prfId,
            approve: checked
        }, function (response) {
            if (response) resolve();
            else reject();
        });
    })
}

const setCommitteeBackground = () => {
    $('.committee-approval-checkbox').addClass('bg-danger-light');
}

const sortByApprovals = (items) => {
    items = items.sort((a, b) => getSortValue(a) - getSortValue(b));
    return items;
};

// entryApproval is still counted: legacy PRFs carry one, and it remains a step already done.
export const getSortValue = (item) => {
    let sortValue = 0;
    if (item.entryApproval != null) sortValue++;
    if (item.salesManagerApproval != null) sortValue++;
    if (item.branchManagerApproval != null) sortValue++;
    if (item.regionalManagerApproval != null) sortValue++;
    if (item.cooApproval != null) sortValue++;
    if (item.cfoApproval != null) sortValue++;
    if (item.ceoApproval != null) sortValue++;

    if (isFullyApproved(item)) {
        sortValue += 50;
    }

    return sortValue;
};

export const isFullyApproved = (item) => {
    // A row with no approval level requires nothing from the level-driven tiers.
    const approvalLevel = item.approvalLevel == null ? -1 : Number(item.approvalLevel);
    const isConstruction = item.prfType !== 'Maintenance';

    // The Sales Manager tier depends on the branch, not on the approval level, and the Branch
    // Manager tier is the unconditional floor of the chain.
    if (isConstruction && [10, 11, 12].includes(item.branch) && item.salesManagerApproval == null) {
        return false;
    }
    if (item.branchManagerApproval == null) {
        return false;
    }
    if (approvalLevel > 1 && item.regionalManagerApproval == null) {
        return false;
    }
    if (approvalLevel > 2 && item.cooApproval == null) {
        return false;
    }
    if (approvalLevel > 3 && isConstruction && Number(item.cfoApprovalRequired) === 1 && item.cfoApproval == null) {
        return false;
    }

    return true;
}

const isFilterable = () => {
    if (getTopRole() === CREATOR_ROLE) return false;
    return {
        extra: false,
        operators: {
            string: {
                gt: "Approval"
            }
        },
        ui: approvalFilter
    }
}

// The dropdown only records a choice; buildApprovalFilter turns it into the rules for the
// viewer's role. The values are just sentinels - anything below zero means "show all".
const SHOW_ALL = -1;
const REQUIRES_MY_APPROVAL = 1;

const approvalFilter = (element) => {
    element.kendoDropDownList({
        dataSource: [
            {text: "Show All", value: SHOW_ALL},
            {text: "Require My Approval", value: REQUIRES_MY_APPROVAL}
        ],
        dataTextField: 'text',
        dataValueField: 'value',
        optionLabel: "--Select Value--"
    });
}

// No filter is applied on load: approvers see every PRF for their branch until they choose
// "Require My Approval". The commented-out body this replaced referred to a helper that no
// longer exists.
const setInitialFilter = () => {
}

const setCustomFilters = (e) => {
    const filter = e.filter;

    if (filter == null) {
        setTimeout(function () {
            grid().data("kendoGrid").dataSource.filter({});
        }, 100)
        return;
    }

    const approvalFilter = filter.filters.find(element => element.field === 'approvalLevel');
    const wantsOwnApprovals = approvalFilter != null && approvalFilter.value >= 0;

    // The approval-status column's filter is a two-option dropdown, so the value only says
    // whether the user picked "Require My Approval". What that means is decided per role.
    const roleFilter = wantsOwnApprovals ? buildApprovalFilter(getTopRole()) : null;

    grid().data("kendoGrid").dataSource.filter(roleFilter || filter);
}
